import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Collapse,
  IconButton,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SendIcon from '@mui/icons-material/Send';
import { PlanStep, StepStatus } from '../../../types/generated/graphql';
import { colors } from '../../../theme/colors';

/** Connector context shape (step.context) for async connector steps */
export type ConnectorContextSnapshot = {
  connectorWait?: { connectorType?: string; requestId?: string };
  connectorPollResult?: Record<string, unknown> & { error?: string; status?: string; resolvedAt?: string };
  connectorActivityLog?: Array<{ event: string; at: string; data?: Record<string, unknown>; attempt?: number }>;
  connectorResponse?: Record<string, unknown>;
};

const CONNECTOR_LABELS: Record<string, string> = {
  foia: 'FOIA',
  // Add more connector types as needed
};

function getConnectorLabel(connectorType: string | undefined): string {
  if (!connectorType) return 'External service';
  return CONNECTOR_LABELS[connectorType] ?? connectorType;
}

interface AsyncConnectorActivityCardProps {
  step: PlanStep;
  /** Show compact (e.g. in CompletedContent) vs full card */
  compact?: boolean;
}

/**
 * Timeline and result display for async connector steps.
 * Shows: Request submitted → Polling... → Result / Blocked (and optional response body).
 * Connector-agnostic; labels keyed by connectorType.
 */
export const AsyncConnectorActivityCard: React.FC<AsyncConnectorActivityCardProps> = ({
  step,
  compact = false,
}) => {
  const [showResult, setShowResult] = useState(!compact);
  const ctx = (step.context as ConnectorContextSnapshot) ?? {};
  const { connectorWait, connectorPollResult, connectorActivityLog, connectorResponse } = ctx;
  const connectorType = connectorWait?.connectorType;
  const label = getConnectorLabel(connectorType);

  if (!connectorWait && !connectorPollResult) return null;

  const isBlocked = step.status === StepStatus.Blocked;
  const isWaiting = step.status === StepStatus.WaitingExternal;
  const isCompleted = step.status === StepStatus.Completed;

  // Build timeline from activity log or derive from context
  const events: Array<{ key: string; label: string; at?: string; icon: React.ReactNode; color: string }> = [];
  if (connectorActivityLog?.length) {
    for (const entry of connectorActivityLog) {
      if (entry.event === 'submitted') {
        events.push({
          key: `submitted-${entry.at}`,
          label: `Request submitted to ${label}`,
          at: entry.at,
          icon: <SendIcon sx={{ fontSize: 16 }} />,
          color: colors.blue[600],
        });
      } else if (entry.event === 'poll_attempt') {
        events.push({
          key: `poll-${entry.at}-${entry.attempt ?? 0}`,
          label: `Polling for result${typeof entry.attempt === 'number' ? ` (attempt ${entry.attempt + 1})` : ''}`,
          at: entry.at,
          icon: <ScheduleIcon sx={{ fontSize: 16 }} />,
          color: colors.amber[600],
        });
      } else if (entry.event === 'completed') {
        events.push({
          key: `completed-${entry.at}`,
          label: 'Result received',
          at: entry.at,
          icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
          color: colors.green[600],
        });
      } else if (entry.event === 'blocked') {
        events.push({
          key: `blocked-${entry.at}`,
          label: 'Blocked',
          at: entry.at,
          icon: <ErrorIcon sx={{ fontSize: 16 }} />,
          color: colors.red[600],
        });
      }
    }
  } else {
    // Derive minimal timeline when no log
    if (connectorWait) {
      events.push({
        key: 'submitted',
        label: `Request submitted to ${label}`,
        icon: <SendIcon sx={{ fontSize: 16 }} />,
        color: colors.blue[600],
      });
    }
    if (isWaiting) {
      events.push({
        key: 'polling',
        label: 'Polling for result...',
        icon: <CircularProgress size={14} sx={{ color: colors.amber[600] }} />,
        color: colors.amber[600],
      });
    }
    if (connectorPollResult) {
      events.push({
        key: 'result',
        label: isBlocked ? 'Blocked' : 'Result received',
        at: typeof connectorPollResult.resolvedAt === 'string' ? connectorPollResult.resolvedAt : undefined,
        icon: isBlocked ? <ErrorIcon sx={{ fontSize: 16 }} /> : <CheckCircleIcon sx={{ fontSize: 16 }} />,
        color: isBlocked ? colors.red[600] : colors.green[600],
      });
    }
  }

  const blockReason =
    isBlocked && connectorPollResult
      ? (connectorPollResult.error as string) ||
        (connectorPollResult.status as string) ||
        'Request did not complete in time.'
      : null;

  const resultPayload = connectorResponse ?? connectorPollResult;
  const hasResultBody = resultPayload && Object.keys(resultPayload).length > 0;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        bgcolor: isBlocked ? colors.red[50] : isWaiting ? colors.amber[50] : colors.gray[50],
        borderLeft: `4px solid ${isBlocked ? colors.red[600] : isWaiting ? colors.amber[400] : colors.gray[300]}`,
      }}
    >
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        {isWaiting && 'Waiting for response'}
        {isBlocked && 'Step blocked'}
        {isCompleted && hasResultBody && 'Result'}
      </Typography>
      {blockReason && (
        <Typography variant="body2" color="error.main" sx={{ mb: 1 }}>
          {blockReason}
        </Typography>
      )}

      {/* Timeline */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {events.map((ev) => (
          <Box
            key={ev.key}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: ev.color,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 24 }}>
              {ev.icon}
            </Box>
            <Typography variant="body2" sx={{ color: 'text.primary' }}>
              {ev.label}
            </Typography>
            {ev.at && (
              <Typography variant="caption" color="text.secondary">
                {new Date(ev.at).toLocaleString()}
              </Typography>
            )}
          </Box>
        ))}
      </Box>

      {/* Result body (expandable) */}
      {hasResultBody && !compact && (
        <>
          <IconButton
            size="small"
            onClick={() => setShowResult(!showResult)}
            sx={{ mt: 1, p: 0 }}
            aria-expanded={showResult}
          >
            <ExpandMoreIcon
              sx={{
                transform: showResult ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            />
          </IconButton>
          <Collapse in={showResult}>
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                mt: 0.5,
                bgcolor: colors.card,
                maxHeight: 200,
                overflow: 'auto',
              }}
            >
              <Typography component="pre" variant="caption" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {JSON.stringify(resultPayload, null, 2)}
              </Typography>
            </Paper>
          </Collapse>
        </>
      )}

      {compact && hasResultBody && (
        <>
          <IconButton
            size="small"
            onClick={() => setShowResult(!showResult)}
            sx={{ mt: 0.5 }}
            aria-expanded={showResult}
          >
            <ExpandMoreIcon sx={{ transform: showResult ? 'rotate(180deg)' : 'rotate(0deg)' }} />
          </IconButton>
          <Collapse in={showResult}>
            <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, bgcolor: colors.card, maxHeight: 160, overflow: 'auto' }}>
              <Typography component="pre" variant="caption" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {JSON.stringify(resultPayload, null, 2)}
              </Typography>
            </Paper>
          </Collapse>
        </>
      )}
    </Paper>
  );
};

export default AsyncConnectorActivityCard;
